/**
 * Inline click-to-edit title for a single-name CRM entity in the record header.
 *
 * Renders the title as a click-to-edit field whenever the entity type's display
 * name resolves from one writable property (`ahoy_name` for a deal, `ahoy_title`
 * for a task or meeting — see `resolveTitlePropertyName`) and the viewer holds
 * rename permission; fails closed to a plain read-only title. Delegates the
 * permission/schema gate, draft lifecycle, and optimistic save to
 * `useInlineNameRename`. The dispatcher owns the previous-name strikethrough;
 * this component renders only the current title line.
 */

import { CrmFieldValueInput } from '@/lib/crm/components/fields/CrmFieldValueInput';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useInlineNameRename } from '@/lib/crm/inline-edit/useInlineNameRename';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import {
  TITLE_EDITOR_FIELD_OVERRIDES,
  TitleAnnouncementRegion,
  TitleReadModeContent,
  useTitleEditorAffordance,
} from './titleEditorChrome';

export interface RecordTitleSingleNameEditorProps {
  object: CrmObject;
  /** Current display name rendered in the title. */
  displayName: string;
}

export function RecordTitleSingleNameEditor({
  object,
  displayName,
}: RecordTitleSingleNameEditorProps) {
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);

  // Destructure so individual stable values can be dep-array items (avoids
  // react-hooks/exhaustive-deps warnings from accessing props on `rename`).
  const {
    announcement,
    canRename,
    containerRef,
    draft,
    errorMessage,
    handleBlur,
    handleInputKeyDown,
    isEditing,
    setDraft,
    startEdit,
    status,
    titleSchema,
    triggerRef,
  } = useInlineNameRename({ object, propertySchemas: schema });

  // Focus-on-edit effect, click/keydown handlers, and trigger-ref callback
  // now live in useTitleEditorAffordance (titleEditorChrome.tsx).
  // Previously: useLayoutEffect mirroring InlineEditableFieldRow:431-440.
  const { setTriggerRef, handleTitleClick, handleTitleKeyDown } =
    useTitleEditorAffordance({
      isEditing,
      containerRef,
      triggerRef,
      startEdit,
    });

  return (
    <div>
      {/* Live region always rendered at the top level — outside the edit/read
          branch — so the "Saved" announcement survives the isEditing flip that
          commits and unmounts the editor in the same batch. */}
      <TitleAnnouncementRegion announcement={announcement} />

      {isEditing && titleSchema ? (
        <div
          ref={containerRef}
          onBlur={handleBlur}
          onKeyDown={handleInputKeyDown}
        >
          {/* min-w-12: keeps an emptied field clickable; safe here because
              nothing renders after the input, so a floor wider than the text
              cannot shift anything (see TITLE_EDITOR_FIELD_OVERRIDES). */}
          <div
            className={cx(TITLE_EDITOR_FIELD_OVERRIDES, '[&_input]:min-w-12')}
          >
            <CrmFieldValueInput
              kind='property'
              schema={titleSchema}
              value={draft as string | number | object | null | undefined}
              onChange={setDraft}
              disabled={status === 'saving'}
              size='sm'
              textDisplay='card'
            />
          </div>
          {errorMessage && (
            <p
              role='alert'
              className='mt-1 text-xs text-[var(--color-text-error-primary)]'
            >
              {errorMessage}
            </p>
          )}
        </div>
      ) : (
        <TitleReadModeContent
          canRename={canRename}
          displayName={displayName}
          setTriggerRef={setTriggerRef}
          handleTitleClick={handleTitleClick}
          handleTitleKeyDown={handleTitleKeyDown}
        />
      )}
    </div>
  );
}
